import type { ProductCategory } from "./categories";
import type { ListOccasion } from "./constants";

/**
 * The Listou sticker library: illustrations we generate once, in one house style, and
 * ship as static files. Lists pick a pack; they never generate their own. That keeps every
 * page on-brand, costs nothing per list, and lets one sticker feed many motifs.
 *
 * Files live at `/stickers/<pack>/<sticker>.webp` (and `.png`), served by the web app. The subjects are
 * the prompts the generation script used, kept here so a regenerated pack stays the same
 * set of things. Each pack is exactly nine: the script draws a pack as one 3x3 sheet in a
 * single image request and cuts it apart, so a pack costs one generation, not nine.
 */

export type StickerPack = {
  id: string;
  label: string;
  /** Occasions this pack is suggested for first; any pack works on any list. */
  occasions: ListOccasion[];
  stickers: { id: string; subject: string }[];
};

export const STICKER_PACKS = [
  {
    id: "noel",
    label: "Noël",
    occasions: ["christmas"],
    stickers: [
      { id: "sapin", subject: "a small decorated Christmas tree with a golden star" },
      { id: "cadeau", subject: "a wrapped gift box with a big red ribbon bow" },
      { id: "bonhomme", subject: "a smiling snowman with a red scarf" },
      { id: "renne", subject: "a cute reindeer with a red nose" },
      { id: "sucre-orge", subject: "a red and white striped candy cane" },
      { id: "boule", subject: "a shiny Christmas tree bauble" },
      { id: "bonnet", subject: "a red Santa hat with a white pompom" },
      { id: "chaussette", subject: "a Christmas stocking with snowflakes" },
      { id: "pain-epice", subject: "a gingerbread man cookie" },
    ],
  },
  {
    id: "hiver",
    label: "Hiver douillet",
    occasions: ["christmas"],
    stickers: [
      { id: "mug", subject: "a steaming mug of hot chocolate with marshmallows" },
      { id: "moufles", subject: "a pair of knitted mittens" },
      { id: "flocon", subject: "a big snowflake" },
      { id: "luge", subject: "a wooden sled" },
      { id: "pingouin", subject: "a baby penguin with a scarf" },
      { id: "ours-polaire", subject: "a sleepy polar bear cub" },
      { id: "bougie", subject: "a lit candle with a sprig of holly" },
      { id: "pull", subject: "a cozy knitted jumper with a reindeer pattern" },
      { id: "chalet", subject: "a tiny snowy wooden cabin" },
    ],
  },
  {
    id: "dinos",
    label: "Dinosaures",
    occasions: ["birthday", "christmas"],
    stickers: [
      { id: "trex", subject: "a friendly green baby T-rex waving" },
      { id: "diplo", subject: "a smiling long-neck diplodocus" },
      { id: "tricera", subject: "a cute orange triceratops" },
      { id: "oeuf", subject: "a spotted dinosaur egg cracking open" },
      { id: "volcan", subject: "a small cartoon volcano puffing a cloud" },
      { id: "feuille", subject: "a big tropical fern leaf" },
      { id: "stego", subject: "a purple stegosaurus" },
      { id: "ptero", subject: "a flying pterodactyl" },
      { id: "empreinte", subject: "a dinosaur footprint" },
    ],
  },
  {
    id: "licornes",
    label: "Licornes",
    occasions: ["birthday", "christmas"],
    stickers: [
      { id: "licorne", subject: "a white unicorn with a pastel mane" },
      { id: "arc-en-ciel", subject: "a pastel rainbow with two little clouds" },
      { id: "etoile", subject: "a smiling yellow star" },
      { id: "nuage", subject: "a fluffy smiling cloud" },
      { id: "coeur", subject: "a pink heart with a small sparkle" },
      { id: "cupcake", subject: "a cupcake with pink frosting and sprinkles" },
      { id: "couronne", subject: "a small golden tiara" },
      { id: "baguette", subject: "a magic wand with sparkles" },
      { id: "sucette", subject: "a swirly rainbow lollipop" },
    ],
  },
  {
    id: "bebe",
    label: "Bébé",
    occasions: ["birth"],
    stickers: [
      { id: "biberon", subject: "a baby bottle" },
      { id: "nounours", subject: "a small teddy bear sitting" },
      { id: "hochet", subject: "a wooden baby rattle" },
      { id: "body", subject: "a tiny baby bodysuit hanging on a line" },
      { id: "lune", subject: "a sleepy crescent moon with a nightcap" },
      { id: "cigogne", subject: "a stork carrying a small bundle" },
      { id: "tetine", subject: "a baby pacifier" },
      { id: "chaussons", subject: "a pair of tiny knitted booties" },
      { id: "poussette", subject: "a vintage baby pram" },
    ],
  },
  {
    id: "mariage",
    label: "Mariage",
    occasions: ["wedding"],
    stickers: [
      { id: "alliances", subject: "two intertwined golden wedding rings" },
      { id: "bouquet", subject: "a bouquet of white and blush roses" },
      { id: "champagne", subject: "two clinking champagne glasses" },
      { id: "colombes", subject: "two white doves forming a heart" },
      { id: "gateau", subject: "a three-tier white wedding cake" },
      { id: "enveloppe", subject: "a sealed envelope with a heart wax seal" },
      { id: "voiture", subject: "a vintage car with tin cans and a just married ribbon" },
      { id: "noeud", subject: "an elegant silk ribbon bow" },
      { id: "pivoine", subject: "a single blush peony" },
    ],
  },
  {
    id: "fete",
    label: "Fête",
    occasions: ["birthday", "other"],
    stickers: [
      { id: "gateau", subject: "a birthday cake with lit candles" },
      { id: "ballons", subject: "a bunch of three colorful balloons" },
      { id: "chapeau", subject: "a striped party hat with a pompom" },
      { id: "confettis", subject: "a party popper bursting confetti" },
      { id: "cadeau", subject: "a small gift box with polka dots" },
      { id: "guirlande", subject: "a short bunting garland of triangle flags" },
      { id: "glace", subject: "an ice cream cone with three scoops" },
      { id: "pinata", subject: "a colorful llama pinata" },
      { id: "etoile", subject: "a shiny gold star balloon" },
    ],
  },
  {
    id: "espace",
    label: "Espace",
    occasions: ["birthday", "christmas"],
    stickers: [
      { id: "fusee", subject: "a red and white cartoon rocket" },
      { id: "planete", subject: "a ringed planet like Saturn" },
      { id: "astronaute", subject: "a small cute astronaut floating" },
      { id: "lune", subject: "a crescent moon with craters" },
      { id: "ovni", subject: "a friendly flying saucer" },
      { id: "etoile", subject: "a shooting star" },
      { id: "alien", subject: "a cute little green alien waving" },
      { id: "satellite", subject: "a small satellite" },
      { id: "terre", subject: "the planet Earth smiling" },
    ],
  },
  {
    id: "jungle",
    label: "Jungle",
    occasions: ["birth", "birthday"],
    stickers: [
      { id: "lion", subject: "a cute lion cub" },
      { id: "girafe", subject: "a baby giraffe" },
      { id: "singe", subject: "a little monkey hanging from a vine" },
      { id: "toucan", subject: "a toucan" },
      { id: "palmier", subject: "a small palm tree" },
      { id: "elephant", subject: "a baby elephant" },
      { id: "zebre", subject: "a baby zebra" },
      { id: "tigre", subject: "a tiger cub" },
      { id: "perroquet", subject: "a colorful parrot" },
    ],
  },
  {
    id: "ocean",
    label: "Océan",
    occasions: ["birth", "birthday"],
    stickers: [
      { id: "baleine", subject: "a smiling blue whale spouting water" },
      { id: "poisson", subject: "a round orange fish" },
      { id: "pieuvre", subject: "a pink octopus" },
      { id: "coquillage", subject: "a scallop seashell" },
      { id: "etoile-mer", subject: "a starfish" },
      { id: "tortue", subject: "a baby sea turtle" },
      { id: "crabe", subject: "a red crab" },
      { id: "dauphin", subject: "a jumping dolphin" },
      { id: "bateau", subject: "a little sailboat" },
    ],
  },
  {
    id: "jardin",
    label: "Jardin",
    occasions: ["birth", "wedding", "other"],
    stickers: [
      { id: "fleur", subject: "a simple daisy flower" },
      { id: "champignon", subject: "a red mushroom with white dots" },
      { id: "abeille", subject: "a fuzzy bumblebee" },
      { id: "papillon", subject: "a pastel butterfly" },
      { id: "coccinelle", subject: "a ladybug" },
      { id: "tulipe", subject: "a pink tulip" },
      { id: "arrosoir", subject: "a vintage watering can" },
      { id: "escargot", subject: "a smiling snail" },
      { id: "tournesol", subject: "a sunflower" },
    ],
  },
  {
    id: "foret",
    label: "Forêt",
    occasions: ["birth", "christmas", "birthday"],
    stickers: [
      { id: "renard", subject: "a baby fox" },
      { id: "herisson", subject: "a baby hedgehog" },
      { id: "ecureuil", subject: "a squirrel holding an acorn" },
      { id: "hibou", subject: "a little owl" },
      { id: "biche", subject: "a baby deer" },
      { id: "gland", subject: "an acorn" },
      { id: "sapin", subject: "a small pine tree" },
      { id: "lapin", subject: "a bunny" },
      { id: "ours", subject: "a brown bear cub" },
    ],
  },
  {
    id: "pirates",
    label: "Pirates",
    occasions: ["birthday"],
    stickers: [
      { id: "bateau", subject: "a pirate ship" },
      { id: "coffre", subject: "an open treasure chest with gold coins" },
      { id: "perroquet", subject: "a pirate parrot with an eyepatch" },
      { id: "carte", subject: "a rolled treasure map" },
      { id: "longue-vue", subject: "a brass telescope" },
      { id: "ancre", subject: "a ship anchor" },
      { id: "chapeau", subject: "a pirate hat with a skull" },
      { id: "ile", subject: "a tiny island with a palm tree" },
      { id: "boussole", subject: "a compass" },
    ],
  },
  {
    id: "chateau",
    label: "Contes",
    occasions: ["birthday", "christmas"],
    stickers: [
      { id: "chateau", subject: "a pink fairytale castle" },
      { id: "dragon", subject: "a friendly baby dragon" },
      { id: "chevalier", subject: "a small knight with a shield" },
      { id: "fee", subject: "a little fairy with wings" },
      { id: "grenouille", subject: "a frog prince with a crown" },
      { id: "carrosse", subject: "a pumpkin carriage" },
      { id: "epee", subject: "a toy wooden sword" },
      { id: "livre", subject: "an open storybook with sparkles" },
      { id: "champignon-maison", subject: "a mushroom house" },
    ],
  },
  {
    id: "vehicules",
    label: "Véhicules",
    occasions: ["birthday", "christmas"],
    stickers: [
      { id: "voiture", subject: "a red toy race car" },
      { id: "camion-pompier", subject: "a fire truck" },
      { id: "tracteur", subject: "a green tractor" },
      { id: "train", subject: "a toy steam train" },
      { id: "avion", subject: "a small propeller plane" },
      { id: "pelleteuse", subject: "a yellow digger" },
      { id: "helicoptere", subject: "a helicopter" },
      { id: "velo", subject: "a kids bicycle" },
      { id: "bus", subject: "a yellow school bus" },
    ],
  },
  {
    id: "gourmandises",
    label: "Gourmandises",
    occasions: ["birthday", "other"],
    stickers: [
      { id: "donut", subject: "a pink frosted donut with sprinkles" },
      { id: "macaron", subject: "a pastel macaron" },
      { id: "croissant", subject: "a golden croissant" },
      { id: "fraise", subject: "a strawberry" },
      { id: "bonbon", subject: "a wrapped candy" },
      { id: "gaufre", subject: "a waffle with cream" },
      { id: "chocolat", subject: "a chocolate bar" },
      { id: "pasteque", subject: "a watermelon slice" },
      { id: "cookie", subject: "a chocolate chip cookie" },
    ],
  },
  {
    id: "voyage",
    label: "Voyage",
    occasions: ["wedding", "other"],
    stickers: [
      { id: "valise", subject: "a vintage suitcase with stickers" },
      { id: "avion", subject: "a passenger airplane" },
      { id: "globe", subject: "a desk globe" },
      { id: "appareil", subject: "a vintage film camera" },
      { id: "tour-eiffel", subject: "the Eiffel Tower" },
      { id: "palmier", subject: "a palm tree on a beach" },
      { id: "montgolfiere", subject: "a hot air balloon" },
      { id: "passeport", subject: "a passport with a ticket" },
      { id: "tente", subject: "a camping tent" },
    ],
  },
  {
    id: "musique",
    label: "Musique",
    occasions: ["birthday", "other"],
    stickers: [
      { id: "guitare", subject: "an acoustic guitar" },
      { id: "note", subject: "a music note" },
      { id: "tambour", subject: "a toy drum" },
      { id: "micro", subject: "a vintage microphone" },
      { id: "casque", subject: "headphones" },
      { id: "vinyle", subject: "a vinyl record" },
      { id: "ukulele", subject: "a ukulele" },
      { id: "piano", subject: "a small toy piano" },
      { id: "trompette", subject: "a trumpet" },
    ],
  },
] as const satisfies readonly StickerPack[];

export type StickerPackId = (typeof STICKER_PACKS)[number]["id"];

/**
 * The gift catalogue's own stickers: one per category (the sticker id is the category key),
 * plus a few for the catalogue's hub. Same house style and same 3x3 sheets as the packs
 * above, but never offered to a list: they illustrate shelves, not occasions.
 *
 * Packs are grouped by section so a sheet draws related things together. A new category
 * goes in the last pack with room, or in a new one; a sticker never moves between packs,
 * since its file lives under its pack.
 */
export const CATALOGUE_STICKER_PACKS = [
  {
    id: "rayons-trousseau",
    stickers: [
      { id: "gigoteuses", subject: "a cozy baby sleeping bag with a pattern of little stars" },
      { id: "vetements", subject: "a tiny knitted baby cardigan with wooden buttons" },
      { id: "langes", subject: "a folded muslin swaddle cloth with a small leaf print" },
      { id: "sucettes", subject: "a baby pacifier attached to a beaded pacifier clip" },
      { id: "souvenirs", subject: "a baby memory book with a tiny footprint on the cover" },
      { id: "lits", subject: "a wooden baby crib with a small blanket over the rail" },
      { id: "literie", subject: "a soft baby pillow on a neatly folded quilt" },
      { id: "meubles", subject: "a small wooden chest of drawers with round knobs" },
      { id: "rangement", subject: "a woven storage basket with a toy peeking out" },
    ],
  },
  {
    id: "rayons-chambre",
    stickers: [
      { id: "veilleuses", subject: "a glowing night light shaped like a sleepy bunny" },
      { id: "decoration", subject: "a small hanging garland of felt stars and clouds" },
      { id: "mobiles", subject: "a baby crib mobile with hanging clouds and a moon" },
      { id: "babyphones", subject: "a baby monitor with a small screen showing a heart" },
      { id: "humidificateurs", subject: "a round humidifier puffing a little cloud of mist" },
      { id: "tapis-eveil", subject: "a baby play gym arch with hanging wooden toys" },
      { id: "doudous", subject: "a floppy bunny comforter blanket" },
      { id: "hochets", subject: "a wooden teething ring rattle with a silicone bead" },
      { id: "premiers-jouets", subject: "a colorful wooden stacking ring toy" },
    ],
  },
  {
    id: "rayons-repas",
    stickers: [
      { id: "transats", subject: "a cozy baby bouncer seat" },
      { id: "parcs", subject: "a square wooden baby playpen" },
      { id: "trotteurs-porteurs", subject: "a wooden baby push walker carrying colorful blocks" },
      { id: "biberons", subject: "a baby bottle of milk with a little drop" },
      { id: "preparation-biberons", subject: "a bottle warmer holding a baby bottle" },
      { id: "allaitement", subject: "a crescent-shaped nursing pillow" },
      { id: "chaises-hautes", subject: "a wooden baby high chair" },
      {
        id: "robots-cuiseurs",
        subject: "a baby food steamer blender next to a small jar of puree",
      },
      { id: "vaisselle", subject: "a baby suction plate with a spoon and a sippy cup" },
    ],
  },
  {
    id: "rayons-sorties",
    stickers: [
      { id: "bavoirs", subject: "a baby bib with a cute fruit print" },
      { id: "bain", subject: "a baby bath tub with a rubber duck and bubbles" },
      { id: "change", subject: "a changing mat with a folded diaper" },
      { id: "soins", subject: "a soft baby hairbrush next to a small bottle of lotion" },
      { id: "securite", subject: "a white baby safety gate" },
      { id: "poussettes", subject: "a modern baby stroller" },
      { id: "chancelieres", subject: "a cozy quilted stroller footmuff" },
      { id: "sacs-a-langer", subject: "a diaper backpack with a bottle in the side pocket" },
      { id: "portage", subject: "a soft baby carrier" },
    ],
  },
  {
    id: "rayons-jouets",
    stickers: [
      { id: "sieges-auto", subject: "a baby car seat" },
      { id: "lits-parapluie", subject: "a folding travel cot" },
      {
        id: "accessoires-voyage",
        subject: "a small travel pouch with a sun hat and a baby bottle",
      },
      { id: "jeux-de-societe", subject: "a board game box with dice and two pawns" },
      { id: "construction", subject: "a small stack of colorful building bricks" },
      { id: "imitation", subject: "a wooden toy kitchen stove with a tiny pot" },
      { id: "poupees", subject: "a rag doll with yarn hair" },
      { id: "vehicules", subject: "a wooden toy train on a small curved track" },
      { id: "figurines", subject: "small animal figurines around a tiny barn" },
    ],
  },
  {
    id: "rayons-grands",
    stickers: [
      { id: "loisirs-creatifs", subject: "a paint palette with a brush and crayons" },
      { id: "musique", subject: "a rainbow toy xylophone with two mallets" },
      { id: "jeux-educatifs", subject: "a wooden shape-sorting puzzle" },
      { id: "plein-air", subject: "a kids balance bike" },
      { id: "deguisements", subject: "a superhero cape with a little mask" },
      { id: "livres", subject: "a stack of picture books" },
      { id: "bagagerie", subject: "a small school backpack with a pencil case" },
      { id: "maman", subject: "a steaming mug next to a scented candle and a flower" },
      { id: "maison", subject: "a cooking pot with a wooden spoon" },
    ],
  },
  {
    id: "rayons-cadeaux",
    stickers: [
      { id: "bien-etre", subject: "a small bottle of body oil with a eucalyptus sprig" },
      { id: "cadeau", subject: "a wrapped gift box with a big bow" },
      { id: "liste", subject: "a paper wish list with a pencil and a heart" },
      { id: "loupe", subject: "a magnifying glass" },
      { id: "coeur", subject: "a plump red heart" },
      { id: "etiquette", subject: "a gift tag on a string" },
      { id: "sac", subject: "a paper shopping bag with a heart on it" },
      { id: "etoiles", subject: "three sparkling stars" },
      { id: "pouce", subject: "a thumbs up hand" },
    ],
  },
  {
    id: "rayons-souvenirs",
    // Spares, drawn now so a future shelf or selection page ("Coffrets naissance",
    // "Bijoux de naissance") finds its sticker already in the house style.
    stickers: [
      {
        id: "coffrets",
        subject: "an open gift box holding baby clothes, a small toy and a ribbon",
      },
      { id: "bijoux", subject: "a small golden birth medal on a thin chain with a tiny star" },
      { id: "tirelire", subject: "a cute ceramic piggy bank" },
      {
        id: "prenom",
        subject: "wooden letter blocks spelling nothing, with small painted flowers",
      },
      { id: "boite-musique", subject: "a small vintage music box with a tiny dancing bunny" },
      { id: "album-photo", subject: "a baby photo album with a heart on the cover" },
      { id: "chaussons", subject: "a pair of soft leather baby slippers" },
      { id: "plaid", subject: "a rolled knitted baby blanket tied with a ribbon" },
      { id: "carte", subject: "a greeting card envelope with a small heart seal" },
    ],
  },
  {
    id: "rayons-adultes",
    // The grown-ups' shelves; "bijoux" draws on its spare in rayons-souvenirs.
    stickers: [
      { id: "mode", subject: "a folded cozy knit sweater with a striped scarf" },
      { id: "chaussures", subject: "a pair of white sneakers with colorful laces" },
      { id: "parfums", subject: "an elegant perfume bottle with a round cap" },
      { id: "romans", subject: "a stack of novels with a bookmark ribbon" },
      {
        id: "musique-films",
        subject: "a vinyl record half out of its sleeve, next to a film clapperboard",
      },
      { id: "jeux-video", subject: "a video game controller" },
      { id: "high-tech", subject: "a pair of wireless headphones next to a smartphone" },
      { id: "sport", subject: "a pair of running shoes with a water bottle" },
      { id: "experiences", subject: "two concert tickets tied with a ribbon" },
    ],
  },
] as const satisfies readonly { id: string; stickers: { id: string; subject: string }[] }[];

const CATALOGUE_PACK_OF = new Map<string, string>(
  CATALOGUE_STICKER_PACKS.flatMap((pack) =>
    pack.stickers.map((sticker) => [sticker.id, pack.id] as const),
  ),
);

type CatalogueStickerId = (typeof CATALOGUE_STICKER_PACKS)[number]["stickers"][number]["id"];
// Every category has its sticker: adding a category without one fails the typecheck here.
const everyCategoryHasASticker: ProductCategory extends CatalogueStickerId ? true : never = true;
void everyCategoryHasASticker;

/** A category's sticker, or one of the hub's (`cadeau`, `loupe`, …). */
export const catalogueStickerUrl = (id: CatalogueStickerId) =>
  stickerUrl(CATALOGUE_PACK_OF.get(id) ?? "", id);

/**
 * The fruits and vegetables the pregnancy tools compare the baby to. Named by what they
 * show, not by week: which week uses which is the page's choice (apps/web
 * src/lib/baby-weeks.ts). Each array is in its sheet's drawing order, which --recut
 * relies on.
 */
export const PREGNANCY_STICKER_PACKS = [
  {
    id: "grossesse-1",
    stickers: [
      { id: "sesame", subject: "a single tiny sesame seed, shown large" },
      { id: "lentille", subject: "a single green lentil, shown large" },
      { id: "myrtille", subject: "a single fresh blueberry with a leaf" },
      { id: "framboise", subject: "a single ripe raspberry" },
      { id: "cerise", subject: "a single red cherry with its stem" },
      { id: "fraise", subject: "a single ripe strawberry" },
      { id: "figue", subject: "a fresh purple fig, one half cut open" },
      { id: "citron-vert", subject: "a whole lime with a small leaf" },
      { id: "citron", subject: "a whole yellow lemon with a leaf" },
    ],
  },
  {
    id: "grossesse-2",
    stickers: [
      { id: "peche", subject: "a ripe peach with a leaf" },
      { id: "pomme", subject: "a shiny red apple with a leaf" },
      { id: "avocat", subject: "an avocado, one half cut open with its pit" },
      { id: "poire", subject: "a ripe green pear" },
      { id: "poivron", subject: "a red bell pepper" },
      { id: "mangue", subject: "a ripe mango" },
      { id: "banane", subject: "a ripe yellow banana" },
      { id: "carotte", subject: "a carrot with its green leafy top" },
      { id: "papaye", subject: "a papaya, one half cut open showing its seeds" },
    ],
  },
  {
    id: "grossesse-3",
    stickers: [
      { id: "pamplemousse", subject: "a pink grapefruit, one half cut open" },
      { id: "mais", subject: "an ear of corn in its husk" },
      { id: "chou-fleur", subject: "a cauliflower head with green leaves" },
      { id: "laitue", subject: "a head of green lettuce" },
      { id: "chou", subject: "a green cabbage" },
      { id: "aubergine", subject: "a purple eggplant" },
      { id: "butternut", subject: "a butternut squash" },
      { id: "chou-milan", subject: "a large savoy cabbage" },
      { id: "noix-de-coco", subject: "a coconut, one half cracked open" },
    ],
  },
  {
    id: "grossesse-4",
    stickers: [
      { id: "potimarron", subject: "an orange red kuri squash" },
      { id: "ananas", subject: "a whole pineapple with its crown" },
      { id: "melon", subject: "a cantaloupe melon, one slice cut out" },
      { id: "romaine", subject: "a head of romaine lettuce" },
      { id: "poireaux", subject: "a bunch of leeks tied with string" },
      { id: "blettes", subject: "a bunch of rainbow chard tied with string" },
      { id: "rhubarbe", subject: "a bunch of pink rhubarb stalks tied with string" },
      { id: "pasteque", subject: "a small round watermelon" },
      { id: "citrouille", subject: "a big orange pumpkin" },
    ],
  },
  {
    id: "grossesse-5",
    stickers: [
      { id: "raisin", subject: "a large bunch of green grapes with a vine leaf" },
      { id: "regime-bananes", subject: "a big bunch of yellow bananas still on their stalk" },
      { id: "courge-spaghetti", subject: "a large yellow spaghetti squash" },
      { id: "melon-vert", subject: "a large green honeydew melon" },
      {
        id: "pommes-de-terre",
        subject: "a burlap sack full of potatoes, a few potatoes beside it",
      },
      { id: "oranges", subject: "a mesh bag full of oranges" },
      { id: "jacquier", subject: "a whole green spiky jackfruit" },
      { id: "potiron", subject: "a huge flat ribbed pale orange pumpkin" },
      { id: "chou-rouge", subject: "a red cabbage" },
    ],
  },
] as const satisfies readonly { id: string; stickers: { id: string; subject: string }[] }[];

export type PregnancyStickerId = (typeof PREGNANCY_STICKER_PACKS)[number]["stickers"][number]["id"];

const PREGNANCY_PACK_OF = new Map<string, string>(
  PREGNANCY_STICKER_PACKS.flatMap((pack) =>
    pack.stickers.map((sticker) => [sticker.id, pack.id] as const),
  ),
);

export const pregnancyStickerUrl = (id: PregnancyStickerId) =>
  stickerUrl(PREGNANCY_PACK_OF.get(id) ?? "", id);

/**
 * One-off stickers of the site's own pages (a landing's padlock), drawn one by one in the
 * packs' house style rather than as a sheet of nine: `generate-stickers.ts --site`.
 */
export const SITE_STICKERS = [
  {
    id: "cadenas",
    subject:
      "a cute closed padlock, raspberry pink body with a small keyhole, a golden butter-yellow shackle",
  },
] as const satisfies readonly { id: string; subject: string }[];

export const siteStickerUrl = (id: (typeof SITE_STICKERS)[number]["id"]) => stickerUrl("site", id);

/** WebP for pages. The same path with `.png` exists for the server's image renderer. */
export const stickerUrl = (packId: string, stickerId: string) =>
  `/stickers/${packId}/${stickerId}.webp`;

/**
 * Motif layouts. A motif is a pack drawn in one of these arrangements over the list's
 * palette, so ten packs give forty motifs, times six palettes. The tile images are
 * precomposed by the same script (`/stickers/<pack>/motif-<layout>.png`).
 */
export const MOTIF_LAYOUTS = [
  { id: "semis", label: "Semis" },
  { id: "dense", label: "Serré" },
  { id: "diagonale", label: "Diagonale" },
  { id: "bordure", label: "Bordure" },
] as const;

export type MotifLayoutId = (typeof MOTIF_LAYOUTS)[number]["id"];

export const motifTileUrl = (packId: string, layout: MotifLayoutId) =>
  `/stickers/${packId}/motif-${layout}.webp`;

export const isStickerPack = (value: string): value is StickerPackId =>
  STICKER_PACKS.some((pack) => pack.id === value);

export const isMotifLayout = (value: string): value is MotifLayoutId =>
  MOTIF_LAYOUTS.some((layout) => layout.id === value);
